import type { CSSProperties } from "react";
import { contrastRatio, hexToRgb } from "@/theme/colorMath";
import type { ProgramPageTheme } from "@/types/programPage";

/**
 * A program page is part of the app: Inter, the semantic surface tokens and
 * the portal's brand accent, following light or dark like every other
 * screen. Everything is exposed as `--pp-*` variables on the page root so
 * the renderer has one vocabulary whether a colour is inherited or a brand
 * overrode it with a hex.
 *
 * Derived shades (muted text, borders, text on the accent) come from the
 * chosen colours, so an override cannot leave the page illegible.
 */

const INK = "#0b0b0c";
const PAPER = "#ffffff";

/** Black or white, whichever reads better on `hex`. */
export function readableOn(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return PAPER;
  return contrastRatio(rgb, hexToRgb(INK)!) >= contrastRatio(rgb, hexToRgb(PAPER)!)
    ? INK
    : PAPER;
}

/** WCAG contrast between two hexes, or null if either does not parse. */
export function contrastBetween(a: string, b: string): number | null {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  return x && y ? contrastRatio(x, y) : null;
}

export function pageThemeStyle(theme: ProgramPageTheme): CSSProperties {
  const { background, surface, text, accent } = theme.colors;
  const custom = background || surface || text;
  const bg = background ?? "hsl(var(--background))";
  const fg = text ?? "hsl(var(--foreground))";
  return {
    "--pp-bg": bg,
    "--pp-surface": surface ?? "hsl(var(--card))",
    "--pp-text": fg,
    "--pp-accent": accent ?? "rgb(var(--brand-600))",
    "--pp-on-accent": accent ? readableOn(accent) : "rgb(var(--brand-contrast))",
    "--pp-accent-soft": accent
      ? `color-mix(in srgb, ${accent} 14%, transparent)`
      : "rgb(var(--brand-100))",
    // With inherited colours the app's own muted/border tokens are exact;
    // once a brand overrides one, derive from what it chose.
    "--pp-muted": custom ? `color-mix(in srgb, ${fg} 62%, ${bg})` : "hsl(var(--muted-foreground))",
    "--pp-border": custom ? `color-mix(in srgb, ${fg} 14%, ${bg})` : "hsl(var(--border))",
    background: bg,
    color: fg,
  } as CSSProperties;
}
